import React, { useContext, useEffect, useState, useReducer, createContext, useMemo } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { Status } from '../common/constants';
import apiService from '../services.js/apiService';

export const PromoCodeContext = createContext(null);

export const usePromoCode = () => {
  const context = useContext(PromoCodeContext);

  return context;
};

const actionType = {
  SET_FILTER:   'SET_FILTER',
  RESET_FILTER: 'RESET_FILTER',
  NEXT_PAGE:    'NEXT_PAGE',
  PREV_PAGE:    'PREV_PAGE',
};

const initFilter = {
  pageNo:              0,
  pageSize:            10,
  code:                '',
  giftId:              '',
  status:              '',
  createdAtStartRange: '',
  createdAtEndRange:   '',
  expiryDate:          '',
};

const filterReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case actionType.SET_FILTER:
      return { ...state, ...payload };
    case actionType.RESET_FILTER:
      // eslint-disable-next-line no-case-declarations
      const resettedKeys = Object.keys(initFilter).reduce((acc, key) => {
        if (!payload.includes(key)) {
          acc[key] = initFilter[key];
        }
        return acc;
      }, {});

      return { ...state, ...resettedKeys };
    case actionType.NEXT_PAGE:
      return { ...state, page: state + 1 };
    case actionType.PREV_PAGE:
      return { ...state, page: state - 1 };
    default:
      return state;
  }
};

export function PromoCodeProvider({ children, match }) {
  const [isLoading, setIsLoading] = useState(false);
  const [filter, dispatchFilter] = useReducer(filterReducer, initFilter);
  const [gifts, setGifts] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);

  const visiblePromoCodes = useMemo(
    () => promoCodes.slice(filter.pageNo * filter.pageSize, filter.pageNo * filter.pageSize + filter.pageSize),
    [promoCodes, filter.pageNo, filter.pageSize],
  );

  const onFilterChange = (params) => {
    // Ensure to search related params on first page
    if ('code' in params || 'giftId' in params || 'status' in params) {
      params.pageNo = 0;
    }

    dispatchFilter({ type: actionType.SET_FILTER, payload: { ...params } });
  };

  const onResetFilter = (keptKeys = []) => {
    dispatchFilter({ type: actionType.RESET_FILTER, payload: keptKeys });
  };

  const fetchPromoCodes = async () => {
    try {
      setIsLoading(true);
      const result = await apiService.getPromoCodes({
        ...filter,
        pageId:   match.params.id,
        pageNo:   undefined, // Remove pageNo from being sent to backend
        pageSize: undefined, // Remove pageSize from being sent to backend
      });

      setPromoCodes(result);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageGifts = async () => {
    try {
      const result = await apiService.getGiftsByParams(match.params.id, { status: Status.Activated });
      const formatedGifts = _.map(result.rows, (row) => _.assign({ id: row.id, label: row.label }));

      setGifts(formatedGifts);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchPageOutlets = async () => {
    try {
      const res = await apiService.getPageOutlets(match.params.id, { status: Status.Live });
      setOutlets(res);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const onPromoCodeUpload = async (data) => {
    try {
      setIsLoading(true);

      await apiService.createPromoCodes(match.params.id, data);

      fetchPromoCodes();
      return;
    // eslint-disable-next-line no-useless-catch
    } catch (err) {
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const onPromoCodesDelete = async (data) => {
    try {
      setIsLoading(true);

      await apiService.deletePromoCodes(match.params.id, data);

      fetchPromoCodes();
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const onBulkUpdatePromoCodes = async (promoCodesId, data) => {
    try {
      setIsLoading(true);

      await apiService.bulkUpdatePromoCodes(match.params.id, {
        ids: promoCodesId,
        ...data,
      });

      fetchPromoCodes();
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const onPromoCodeUpdate = async (id, data) => {
    try {
      setIsLoading(true);

      await apiService.updatePromoCode(match.params.id, id, data);

      fetchPromoCodes();
      // eslint-disable-next-line no-useless-catch
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPageGifts();
    fetchPageOutlets();
  }, []);

  useEffect(() => {
    fetchPromoCodes();
  }, [filter.giftId, filter.code, filter.status, filter.createdAtStartRange, filter.createdAtEndRange, filter.expiryDate]);

  return (
    <PromoCodeContext.Provider value={{
      isLoading,
      promoCodes,
      visiblePromoCodes,
      gifts,
      outlets,
      filter,
      onFilterChange,
      onResetFilter,
      onPromoCodeUpload,
      onPromoCodesDelete,
      onBulkUpdatePromoCodes,
      onPromoCodeUpdate,
    }}
    >
      {children}
    </PromoCodeContext.Provider>
  );
}

PromoCodeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  match:    PropTypes.shape(Object).isRequired,
};
